/**
 * Live alarm feed (Socket.IO).
 *
 * The API's `live` gateway republishes outbox rows on the `/live` namespace
 * (`apps/api/src/live/live.protocol.ts`). What arrives is a *notice*, not an
 * alarm: `{ outbox_id, type, alarm_id, team_id, created_at, payload }`. The
 * console keeps one shared connection, fans the notices out to whichever views
 * are mounted, and leaves it to the alarm list to fetch the rows it wants — so
 * nothing jumps under the pointer and no half-shaped alarm reaches the table.
 */
import { useEffect, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

export const LIVE_EVENTS = [
  'alarm.created',
  'alarm.updated',
  'alarm.assigned',
  'alarm.resolved',
  'alarm.reopened',
  'alarm.suppression_lifted',
  'alarm.snooze_expired',
  'alarm.unacknowledged',
] as const;
export type LiveEventType = (typeof LIVE_EVENTS)[number];

export interface LiveEvent {
  type: LiveEventType;
  alarmId: string;
  teamId: string | null;
  receivedAt: number;
}

export type LiveListener = (event: LiveEvent) => void;

const NAMESPACE = import.meta.env.VITE_LIVE_NAMESPACE ?? '/live';
const PATH = import.meta.env.VITE_LIVE_PATH ?? '/socket.io';

let socket: Socket | null = null;
let connected = false;
const listeners = new Set<LiveListener>();
const statusListeners = new Set<(connected: boolean) => void>();

function setConnected(next: boolean): void {
  if (connected === next) return;
  connected = next;
  for (const listener of statusListeners) listener(next);
}

/** The alarm id and team a notice refers to, or null if it carries neither. */
function readNotice(payload: unknown): { alarmId: string; teamId: string | null } | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const record = payload as Record<string, unknown>;
  const alarmId = typeof record.alarm_id === 'string' ? record.alarm_id : null;
  if (!alarmId) return null;
  return { alarmId, teamId: typeof record.team_id === 'string' ? record.team_id : null };
}

function ensureSocket(): Socket {
  if (socket) return socket;
  const next = io(NAMESPACE, {
    path: PATH,
    withCredentials: true,
    // WebSocket first because that is what this feed is for, but `polling` is
    // not decoration: a reverse proxy that does not forward the `Upgrade`
    // header — websocket support is an opt-in toggle on NPMplus and Nginx
    // Proxy Manager — fails the websocket handshake, and long-polling is then
    // the only way the live feed works at all.
    transports: ['websocket', 'polling'],
    // engine.io defaults this to false, which means a failed FIRST transport
    // aborts the connection instead of trying the next one. Without it the
    // list above is a lie: behind a proxy without websocket support the feed
    // would never fall back, and the console would sit on "Offline" forever
    // while plain HTTP to the same origin worked perfectly.
    tryAllTransports: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });
  next.on('connect', () => setConnected(true));
  next.on('disconnect', () => setConnected(false));
  next.on('connect_error', () => setConnected(false));
  // The gateway hangs up on an unauthenticated handshake; treat it as offline
  // rather than retrying into a loop the viewer cannot see.
  next.on('unauthorized', () => setConnected(false));
  for (const type of LIVE_EVENTS) {
    next.on(type, (payload: unknown) => {
      const notice = readNotice(payload);
      if (!notice) return;
      const event: LiveEvent = { type, ...notice, receivedAt: Date.now() };
      for (const listener of listeners) listener(event);
    });
  }
  socket = next;
  return next;
}

function releaseSocket(): void {
  if (listeners.size > 0 || statusListeners.size > 0) return;
  socket?.close();
  socket = null;
  setConnected(false);
}

/** Subscribe to live alarm events for as long as the component is mounted. */
export function useLiveAlarms(listener: LiveListener, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    listeners.add(listener);
    ensureSocket();
    return () => {
      listeners.delete(listener);
      releaseSocket();
    };
  }, [listener, enabled]);
}

/** Connection status for the topbar indicator. */
export function useLiveStatus(enabled = true): boolean {
  const [isConnected, setIsConnected] = useState(connected);
  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    statusListeners.add(setIsConnected);
    ensureSocket();
    setIsConnected(connected);
    return () => {
      statusListeners.delete(setIsConnected);
      releaseSocket();
    };
  }, [enabled]);
  return isConnected;
}

/** Test seam: drop the shared connection. */
export function resetLiveFeed(): void {
  listeners.clear();
  statusListeners.clear();
  socket?.close();
  socket = null;
  connected = false;
}
